<template>
	<div
		ref="carousel"
		class="carousel"
		data-cy="carousel"
	>
		<div
			ref="container"
			:class="`carousel__container carousel__container--preview-${preview}`"
		>
			<navigation
				v-if="items.length > currentGrid"
				:current-index="currentIndex"
				:items-length="items.length"
				:grid="currentGrid"
				:navigation="navigation"
				:color="color"
				:gutter="gutter"
				:style="`width: ${currentCardsWidth}px`"
				@decrementIndex="decrementIndex"
				@incrementIndex="incrementIndex"
			/>

			<div
				:class="`carousel__cards-container`"
				:style="{ height: `${cardHeight}px` }"
			>
				<div
					ref="cards"
					class="carousel__cards"
					:style="{
						left: `${position}px`,
						transition: `left ${animationDuration}ms`,
						width: `${carouselCardsWidth}px`,
					}"
				>
					<ul>
						<li
							v-for="(item, index) in items"
							:key="index"
							:style="{
								'background-color': item.color,
								'width': `${cardWidth}px`,
								'margin-right': `${gutter}px`,
							}"
							class="carousel__card"
						>
							<template v-if="$scopedSlots.thumbnail">
								<slot
									name="thumbnail"
									:item="item"
									:index="index"
								/>
								<slot
									v-if="$scopedSlots.thumbnailExtra"
									name="thumbnailExtra"
									:item="item"
									:index="index"
								/>
							</template>

							<thumbnail
								v-else
								:link="item.link"
								:link-text="item.title"
								:thumbnails="item.thumbnails"
								:default-height="defaultHeight"
								@changedDefaultHeight="changeDefaultHeight"
							>
								<template
									v-if="$scopedSlots.thumbnailExtra"
									slot="thumbnailExtra"
								>
									<slot
										name="thumbnailExtra"
										:item="item"
										:index="index"
									/>
								</template>
							</thumbnail>

							<description
								:item="item"
								:color="color"
								:max-excerpt-length="maxExcerptLength"
							>
								<template
									v-if="$scopedSlots.description"
									slot="description"
								>
									<slot
										name="description"
										:item="item"
										:index="index"
									/>
								</template>
							</description>
						</li>
					</ul>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Description from './description/Description.vue';
import Navigation from './navigation/Navigation.vue';
import Thumbnail from './thumbnail/Thumbnail.vue';

export default {
	name: 'Carousel',

	components: {
		Description,
		Navigation,
		Thumbnail,
	},

	props: {
		animationDuration: {
			type: Number,
			required: false,
			default: 500,
			validator: value => value > 0,
			description: 'Sets the duration of the animation of the Carousel in MS',
		},

		color: {
			type: String,
			required: false,
			default: 'accent4-normal',
			description: 'Sets carousel primary color, such as navigation arrow and clickable link in description',
		},

		grid: {
			type: Object,
			required: false,
			default: () => ({
				xlarge: 6,
				large: 5,
				medium: 4,
				small: 3,
				xsmall: 2,
				xxsmall: 1,
			}),
			description: 'Sets how many cols are displayed for each breakpoint',
		},

		gutter: {
			type: Number,
			required: false,
			default: 16,
			description: 'Sets width in pixels between two cols',
		},

		items: {
			type: Array,
			required: true,
			description: 'Sets the array of objects that will be displayed as cards in the carousel',
		},

		maxExcerptLength: {
			type: Number,
			required: false,
			default: null,
			description: 'If provided, cuts excerpt avec maxExcerptLength characters and add "..."',
		},

		navigation: {
			type: Object,
			required: false,
			default: () => ({
				previous: 'angle-left',
				next: 'angle-right',
				color: 'accent1-normal',
			}),
			description: 'Sets defaults for navigation menu',
		},

		preview: {
			type: String,
			required: false,
			default: 'none',
			validator: value => [
				'none',
				'left',
				'right',
				'left-and-right',
			].indexOf(value) !== -1,
			description: 'Sets whether or not previews should be shown next to container',
		},

		step: {
			type: Number,
			required: false,
			default: null,
			description: 'Sets the number of cards that change when user navigates',
		},
	},

	slots: {
		description: {
			required: false,
			scoped: true,
			description: 'Replaces regular decription.',
		},

		thumbnail: {
			required: false,
			scoped: true,
			description: 'Replaces regular thumbnail.',
		},

		thumbnailExtra: {
			required: false,
			scoped: true,
			description: 'Is added on top of the thumbnail',
		},
	},

	data() {
		return {
			currentIndex: 0,
			currentGrid: 0,
			cardWidth: 0,
			cardHeight: 0,
			defaultHeight: 0,
		};
	},

	computed: {
		currentStep() {
			return this.step && this.step <= this.currentGrid ? this.step : this.currentGrid;
		},

		position() {
			const cardOuterWidth = (this.cardWidth + this.gutter);
			const previewOffset = this.preview === 'left' || this.preview === 'left-and-right' ? 35 : 0;
			return (this.currentIndex * -1 * cardOuterWidth) + previewOffset;
		},

		currentCardsWidth() {
			return (this.currentGrid * (this.cardWidth + this.gutter)) - this.gutter;
		},

		carouselCardsWidth() {
			if (this.items.length) {
				return this.items.length * (this.cardWidth + this.gutter);
			}

			return null;
		},

		canDecrement() {
			return this.currentIndex > 0;
		},

		canIncrement() {
			return this.currentIndex + this.currentGrid < this.items.length;
		},
	},

	mounted() {
		const { carousel } = this.$refs;

		if (carousel) {
			carousel.addEventListener('keydown', this.checkForKeyboardNavigation);
		}

		window.addEventListener('resize', this.onResize);

		setTimeout(() => {
			this.onResize();
		}, 200);
	},

	beforeDestroy() {
		const { carousel } = this.$refs;

		if (carousel) {
			carousel.removeEventListener('keydown', this.checkForKeyboardNavigation);
		}

		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		onResize() {
			const { container } = this.$refs;

			if (!container) {
				return;
			}

			const containerWidth = container.offsetWidth;

			this.currentGrid = this.grid.xxsmall;

			if (containerWidth > 1680) {
				this.currentGrid = this.grid.xlarge;
			} else if (containerWidth > 1440) {
				this.currentGrid = this.grid.large;
			} else if (containerWidth > 1200) {
				this.currentGrid = this.grid.medium;
			} else if (containerWidth > 960) {
				this.currentGrid = this.grid.small;
			} else if (containerWidth > 600) {
				this.currentGrid = this.grid.xsmall;
			}

			if (this.items.length < this.currentGrid) {
				this.currentGrid = this.items.length;
			}

			this.setCardWidth();
			this.verifyIndexValidity();
		},

		verifyIndexValidity() {
			if (this.currentIndex + this.currentGrid > this.items.length) {
				this.currentIndex = this.items.length - this.currentGrid;
			}
		},

		setCardWidth() {
			const { container, cards } = this.$refs;

			if (container) {
				let gridDivider = this.currentGrid;

				if (this.currentGrid > this.items.length) {
					gridDivider = this.items.length;
				}

				const width = Math.round(container.offsetWidth / gridDivider);
				const proportionalGutter = this.gutter * ((this.currentGrid - 1) / this.currentGrid);
				const previewBuffer = this.getPreviewBuffer() / gridDivider;

				this.cardWidth = width - proportionalGutter - 2 - previewBuffer;
			}

			if (cards) {
				setTimeout(() => {
					this.cardHeight = cards.offsetHeight;
				}, 500);
			}
		},

		getPreviewBuffer() {
			switch (this.preview) {
				case 'left':
				case 'right':
					return 35;

				case 'left-and-right':
					return 70;

				case 'none':
				default:
					return 0;
			}
		},

		decrementIndex() {
			if (this.currentIndex - this.currentStep >= 0) {
				this.currentIndex -= this.currentStep;
			} else {
				this.currentIndex = 0;
			}
		},

		incrementIndex() {
			if ((this.currentIndex + this.currentGrid + this.currentStep) <= this.items.length) {
				this.currentIndex += this.currentStep;
			} else {
				this.currentIndex = this.items.length - this.currentGrid;
			}
		},

		changeDefaultHeight(height) {
			if (height > 0) {
				this.defaultHeight = height;
			}
		},

		checkForKeyboardNavigation(event) {
			const { keyCode } = event;

			// Left arrow
			if (keyCode === 37 && this.canDecrement) {
				this.decrementIndex();
			}

			// Right arrow
			if (keyCode === 39 && this.canIncrement) {
				this.incrementIndex();
			}
		},
	},
};
</script>

<style lang="scss" scoped src="./carousel.scss" />
